import type { ComponentType } from "react";
import { askForApproval, makeApprovalCardTool } from "./ApprovalCardTool";
import { AskUserQuestionCard } from "./AskUserQuestionCard";
import {
  CSV_TRIGGER_RUN_TOOL_NAME,
  PUBLIC_DATASET_TRIGGER_RUN_TOOL_NAME,
  triggerRunApproval,
  VCF_TRIGGER_RUN_TOOL_NAME,
} from "./dagTrigger";
import type { ToolCallEntry } from "./useTurns";

// Props every human-in-the-loop tool card receives. The card reads the pending
// call from `entry` (interactive while `entry.result` is undefined, read-only
// once set) and submits the user's response via `onAnswer`, which resumes the
// paused run.
export interface HitlToolProps {
  entry: ToolCallEntry;
  // Whether the agent run is still in flight. Lets a card tell "the tool-call
  // args are still streaming in" (show a placeholder) apart from "the args are
  // final but unparseable" (a real error to surface), rather than guessing.
  isRunning: boolean;
  // Resume the paused run with the user's response. Omitted in read-only
  // views (shared / admin), where the card renders but cannot be answered.
  // Resolves when the resume is sent; rejects if it fails, so the card can
  // re-enable Submit for a retry rather than latching on "Submitting…".
  onAnswer?: (toolCallId: string, answer: string) => void | Promise<void>;
  // The other tool calls in the same assistant turn, so a card can render from
  // a preceding tool's result rather than the model's args.
  turnToolCalls?: readonly ToolCallEntry[];
}

// Bespoke cards, keyed by tool name. Which calls are awaiting an answer comes
// from the server, not from this map; a pending call without an entry here gets
// the generic DeferredToolFallbackCard (free-text answer) instead.
export const HITL_TOOL_RENDERERS: Record<string, ComponentType<HitlToolProps>> = {
  ask_user_question: AskUserQuestionCard,
  ask_for_approval: makeApprovalCardTool(askForApproval),
  [PUBLIC_DATASET_TRIGGER_RUN_TOOL_NAME]: makeApprovalCardTool(triggerRunApproval),
  [VCF_TRIGGER_RUN_TOOL_NAME]: makeApprovalCardTool(triggerRunApproval),
  [CSV_TRIGGER_RUN_TOOL_NAME]: makeApprovalCardTool(triggerRunApproval),
};
